import { AssignmentOutlined } from '@mui/icons-material'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppRoutesNavigation } from 'src/app-routes'
import { Chip } from 'src/components/common/Chip'
import { useNavigation } from 'src/context/NavigationContext'
import { IDeviation } from 'src/generated-types/deviation'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { getPlacementArray } from 'src/utils/function'
import { hasDeviationPlacementContent } from './DeviationContextSidebar.action'
import { DeviationLocationMap } from './DeviationLocationMap'
import { DeviationSectionCard } from './DeviationSectionCard'

type Props = {
  deviation?: IDeviation
  customerId?: EntityId
  localityId?: EntityId
}

const EyebrowLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography
      sx={{
        fontSize: '13px',
        fontWeight: 500,
        color: getThemeColor(theme, MUIThemeColors.secondaryMain),
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  )
}

export const DeviationContextSidebar: React.FC<Props> = ({ deviation, customerId, localityId }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()
  const { navigateTo } = useNavigation()

  const placement = useMemo(() => {
    if (deviation?.placement) {
      return getPlacementArray(deviation.placement)
    }
    return undefined
  }, [deviation?.placement])

  const hasNavigation = !!(deviation?.mooringId && deviation?.positionId && deviation?.componentId)

  const mapMooringId = deviation?.mooringId ?? deviation?.effectiveMooringId
  const mapPositionId = deviation?.positionId ?? deviation?.effectivePositionId

  const handlePlacementClick = () => {
    if (!deviation || !hasNavigation) return

    if (deviation.scheduledServiceDeviations && deviation.scheduledServiceDeviations.length > 0) {
      navigateTo(
        AppRoutesNavigation.Localities.localityService(
          customerId as EntityId,
          localityId as EntityId,
          deviation.scheduledServiceDeviations[0].id,
        ),
      )
    }

    navigateTo(
      AppRoutesNavigation.Localities.componentDeviations(
        customerId as EntityId,
        localityId as EntityId,
        deviation.mooringId as number,
        deviation.positionId as number,
        deviation.componentId as number,
      ),
    )
  }

  const hasLinkedWorkOrders = !!(deviation?.linkedWorkOrders && deviation.linkedWorkOrders.length > 0)
  const hasLinkedServices = !!(deviation?.scheduledServiceDeviations && deviation.scheduledServiceDeviations.length > 0)
  const canShowMap = !!(customerId && localityId && mapMooringId)

  if (!hasDeviationPlacementContent(deviation, customerId, localityId)) return null

  return (
    <DeviationSectionCard title={t('placement', { ns: 'common' })} fullHeight>
      <Flex.Column gap={1.5}>
        {/* Facility map highlighting the deviation's cage/position */}
        {canShowMap && (
          <DeviationLocationMap
            customerId={customerId}
            localityId={localityId}
            mooringId={mapMooringId}
            positionId={mapPositionId}
            deviationType={deviation?.type}
          />
        )}

        {/* Plassering breadcrumb */}
        {placement && (
          <Box>
            <EyebrowLabel>{t('placement', { ns: 'common' })}</EyebrowLabel>
            <Tooltip title={hasNavigation ? `${t('PlacementDetail', { ns: 'deviation' })}` : ''}>
              <Typography
                onClick={handlePlacementClick}
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: getThemeColor(theme, MUIThemeColors.primaryMain),
                  textDecoration: hasNavigation ? 'underline' : 'none',
                  cursor: hasNavigation ? 'pointer' : 'default',
                  wordBreak: 'break-word',
                }}
              >
                {placement}
              </Typography>
            </Tooltip>
          </Box>
        )}

        {/* Tilknyttet arbeidsordre */}
        {hasLinkedWorkOrders && (
          <Box>
            <EyebrowLabel>{t('linkedWorkOrders', { ns: 'deviation' })}</EyebrowLabel>
            <Flex.Row gap={0.5} sx={{ flexWrap: 'wrap' }}>
              {deviation!.linkedWorkOrders!.map(wo => (
                <Tooltip key={wo.id} title={wo.title ?? ''} placement="top" enterDelay={300}>
                  <Chip
                    size="small"
                    icon={<AssignmentOutlined fontSize="small" />}
                    label={wo.title ? `#${wo.id} ${wo.title}` : `#${wo.id}`}
                    clickable
                    onClick={() =>
                      navigateTo(
                        AppRoutesNavigation.Localities.workOrder(customerId as EntityId, localityId as EntityId, wo.id),
                      )
                    }
                    variant="outlined"
                    sx={{ maxWidth: '100%' }}
                  />
                </Tooltip>
              ))}
            </Flex.Row>
          </Box>
        )}

        {hasLinkedServices && (
          <Box>
            <EyebrowLabel>{t('linkedServices', { ns: 'deviation' })}</EyebrowLabel>
            <Flex.Row gap={0.5} sx={{ flexWrap: 'wrap' }}>
              {deviation!.scheduledServiceDeviations!.map(sd => (
                <Tooltip key={sd.scheduledServiceId} title={sd.title ?? ''} placement="top" enterDelay={300}>
                  <Chip
                    size="small"
                    icon={<AssignmentOutlined fontSize="small" />}
                    label={sd.title ? `${sd.title}` : `#${sd.scheduledServiceId}`}
                    clickable
                    onClick={() =>
                      navigateTo(
                        AppRoutesNavigation.Localities.localityService(
                          customerId as EntityId,
                          localityId as EntityId,
                          sd.scheduledServiceId,
                        ),
                      )
                    }
                    variant="outlined"
                    sx={{ maxWidth: '100%' }}
                  />
                </Tooltip>
              ))}
            </Flex.Row>
          </Box>
        )}
      </Flex.Column>
    </DeviationSectionCard>
  )
}
